import React, {
    useCallback, useMemo, useState, ReactNode, FC,
} from 'react';
import { useTranslation } from 'react-i18next';
import { object, string, ObjectSchema } from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { generalAgencySelector, usePortfolioSelector } from 'domain/Portfolio';
import AssetFeeModal from 'pages/ClientOverview/pages/Portfolios/components/FeesOverview/components/AssetFeeModal/AssetFeeModal';
import CustodyFeeModal from 'pages/ClientOverview/pages/Portfolios/components/FeesOverview/components/CustodyFeeModal/CustodyFeeModal';
import SelectElement from 'components/AtomicStructure/atoms/SelectElement';
import distinguishClientType from 'widgets/FeesOverview/hooks/distinguishClientType';
import { ICON_TYPE_CLOSE_SLIM } from 'ui-library/components/Icon';
import AdministrationFeeModal from 'pages/ClientOverview/pages/Portfolios/components/FeesOverview/components/AdministrationFeeModal/AdministrationFeeModal';
import findSpecificFeeData from 'widgets/FeesOverview/hooks/findSpecificFeeData';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { generateStaticNumberOptions } from 'utils/arrayUtils';
import { FEE_TYPES } from 'datasource/CreatePortfolio/constants';
import {
    Button, Icon, Loader, Modal, Select,
} from '../../../../../../../../../../ui-library';
import { useYupValidationResolver } from '../../../../../../../../../../hooks/useYupValidationResolver';
import useFormatting from '../../../../../../../../../../locale/useFormatting';
import Preloader from '../../../../../../../../../../components/Preloader';
import notification from '../../../../../../../../../../ui-library/components/Notification';
import PbNumber from '../../../../../../components/PbNumber/PbNumber';
import { useManagerProfile } from '../../../../../../../../../../prodivers/managerProfile';
import { getAgentInfo, joinWithSeparator } from '../../utils';
import { validateInputAlphanumeric } from '../../../../../../../../../../validation/validation';
import { CHANGE_HISTORY_TYPE, DEPOSIT_MODAL_TYPE } from '../../constants';
import ChangeHistoryModal from './ChangeHistoryModal';
import { useSinglePortfolio } from '../../../../context';

import '../../styles/styles.css';
import { FeeData } from '../../../../context/context';

// --- Interfaces ---

interface FzDepotInformationKpiProps {
    portfolioId: string | number;
}

interface GeneralAgency {
    id: string | number;
    name: string;
    vstNumber?: string;
}

interface PlanDetails {
    lpzPlanId?: string;
    accountIban?: string;
    openingDate?: string;
    closingDate?: string;
    issueCommission: number;
    pbNumber?: string;
    memberFullName?: string;
    provisionReceiver?: string;
    generalAgency?: GeneralAgency;
}

interface Agent {
    id: string | number;
    firstName: string;
    lastName: string;
    email: string;
}

interface FormValues {
    newlySelectedValue: string;
    agentId: string | null;
}

const FEES_ACCESS_POLICIES: Record<string, string> = {
    [FEE_TYPES.custody]: 'EnableEditCustodyFee',
    [FEE_TYPES.asset]: 'EnableEditAssetFee',
    [FEE_TYPES.administration]: 'AdministrationFeeChange3aPremium',
};

const getFeeLabel = (feeType: number, t: (key: string) => string): string => {
    switch (feeType) {
        case FEE_TYPES.custody: return t('shadowAccount.depot.custody');
        case FEE_TYPES.asset: return t('shadowAccount.depot.asset');
        case FEE_TYPES.administration: return t('shadowAccount.depot.administration');
        case FEE_TYPES.entry: return t('shadowAccount.depot.entry');
        default: return '-';
    }
};

const FzDepotInformationKpi: FC<FzDepotInformationKpiProps> = ({ portfolioId }) => {
    const { t } = useTranslation();
    const { getFormattedDate, getFormattedNumber } = useFormatting();

    const [agentFilter, setAgentFilter] = useState<string>('');

    const {
        modelPortfolio: dataModelPortfolio,
        portfolioDetailRaw,
        fees: allFees,
        setFees,
        investmentAppPersonalDetails,
        withdrawal,
        isPortfolioOnReadOnly,
        investmentAppId,
        isPk,
        isZivAzp,
        isAzpAlt,
        isZic,
        isAzp,
        is3ap,
        isZifd,
        isZiv,
        lastApplicationDetails,
    } = useSinglePortfolio();

    const { getAgentAndGeneralAgencyData } = usePortfolioSelector(generalAgencySelector);

    const {
        data: generalAgenciesFromAPI,
    } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getGeneralAgencies',
        enabled: true,
        staleTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const {
        data: planDetailsResponse, isLoading: detailsLoading, error: detailsError, refetch: refetchPlanDetails,
    } = useGetCustomService({
        service: 'planService',
        api: 'getPlanDetails',
        payload: { portfolioId },
        enabled: !!portfolioId,
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const {
        data: membersResponse, isLoading: searchAgentsInProgress, error: membersError,
    } = useGetCustomService({
        service: 'customMembersService',
        api: 'getMembers',
        payload: { filter: agentFilter },
        enabled: true,
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const agentsList: Agent[] = membersResponse?.data || [];

    const planDetails = planDetailsResponse?.data as PlanDetails | null;

    const { mutateAsync: planMutation, isPending: saveInProgress } = useGenericAPIMutation();

    // --- State ---
    const [modalVersion, setModalVersion] = useState<string>('');
    const [changeHistoryType, setChangeHistoryType] = useState<string | null>(null);
    const [selectedFeeType, setSelectedFeeType] = useState<string | null>(null);
    const [vstNumber, setVstNumber] = useState<string>('');
    const [vstNumberError, setVstNumberError] = useState<string | null>(null);
    const [feeModal, setFeeModal] = useState<number | null>(null);

    const { hasAccessPolicy } = useManagerProfile();
    const generalAgencies: GeneralAgency[] = generalAgenciesFromAPI?.data || [];

    // --- Memos ---
    const previousAssetManagementFee = useMemo(
        () => lastApplicationDetails?.investmentDescription?.fees?.find((f: FeeData) => f?.feeType === FEE_TYPES.asset)?.fee,
        [lastApplicationDetails?.investmentDescription?.fees],
    );

    const modelPortfolioSettings = useMemo(() => (JSON.parse(dataModelPortfolio?.Attributes ?? '{}')), [dataModelPortfolio?.Attributes]);

    const issueCommissionOptions = useMemo(() => (
        (modelPortfolioSettings?.issueComission ?? []).map((item: any) => ({
            label: getFormattedNumber(item?.label, { maximumFractionDigits: 1, minimumFractionDigits: 1 }),
            id: String(item.value),
        }))
    ), [modelPortfolioSettings?.issueComission, getFormattedNumber]);

    const staticIssuecommissionOptions = useMemo(() => generateStaticNumberOptions({ options: issueCommissionOptions, step: 0.1 }), [issueCommissionOptions]);

    const fees = useMemo(() => allFees?.filter((f: FeeData) => (isZic ? f.feeType === FEE_TYPES.entry : f.feeType !== FEE_TYPES.entry)), [allFees, isZic]);

    const custodyAmFee = useMemo(() => {
        if (isPk) return [];
        let feesOptions = [{ type: FEE_TYPES.custody, isEditable: !isZic }];

        if (isZic) feesOptions = [{ type: FEE_TYPES.entry, isEditable: false }];
        if (isZivAzp || isAzpAlt) feesOptions.push({ type: FEE_TYPES.asset, isEditable: true });
        if (is3ap) feesOptions = [{ type: FEE_TYPES.administration, isEditable: true }];

        return feesOptions;
    }, [isZivAzp, isPk, isZic, isAzpAlt, is3ap]);

    const agentsListOptions = useMemo(() => [
        { value: '', label: t('position.pleaseSelect') },
        ...agentsList.map(i => ({ value: String(i.id), label: `${i.firstName} ${i.lastName} (${i.email})` })),
    ], [agentsList, t]);

    const investedAmount = useMemo(() => (isAzp ? withdrawal?.investmentAmount ?? 0 : portfolioDetailRaw?.InvestedAmount), [withdrawal?.investmentAmount, isAzp, portfolioDetailRaw?.InvestedAmount]);

    const clientType = useMemo(
        () => distinguishClientType(investmentAppPersonalDetails?.zurichEmployee, investmentAppPersonalDetails?.destinataryVita),
        [investmentAppPersonalDetails?.zurichEmployee, investmentAppPersonalDetails?.destinataryVita],
    );

    const { data: assetManagementResponse } = useGetCustomService({
        service: 'planService',
        api: 'getAssetManagement',
        payload: {
            modelPortfolioId: dataModelPortfolio?.Id,
            investedAmount: investedAmount ?? 0,
            clientType,
        },
        enabled: !!(isZivAzp && dataModelPortfolio?.Id && clientType),
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const defaultAssetFee = assetManagementResponse?.data?.standardFee as number | undefined;

    // --- Callbacks ---
    const getAgentInformation = (filter: string) => {
        setAgentFilter(filter);
    };

    const updatePbNumber = useCallback(async (values: FormValues) => {
        try {
            await planMutation({
                service: 'planService',
                API: 'updatePbNumber',
                payload: {
                    portfolioId, pbNumber: values.newlySelectedValue, agentId: values.agentId, vstNumber,
                },
            });
            refetchPlanDetails();
            getAgentAndGeneralAgencyData();
            notification.open({ content: t('shadowAccount.depot.pbNumberSuccessfullyChanged'), type: 'success' });
            setModalVersion('');
        } catch {
            // error notification handled by useGenericAPIMutation onError
        }
    }, [portfolioId, planMutation, refetchPlanDetails, vstNumber, getAgentAndGeneralAgencyData, t]);

    const updateGeneralAgency = useCallback(async (generalAgency: string) => {
        try {
            await planMutation({
                service: 'planService',
                API: 'updateGeneralAgency',
                payload: { portfolioId, generalAgency },
            });
            refetchPlanDetails();
            getAgentAndGeneralAgencyData();
            notification.open({ content: t('shadowAccount.depot.generalAgencySuccessfullyChanged'), type: 'success' });
            setModalVersion('');
        } catch {
            // error notification handled by useGenericAPIMutation onError
        }
    }, [portfolioId, planMutation, refetchPlanDetails, getAgentAndGeneralAgencyData, t]);

    const updateFeesLocally = useCallback((payload: FeeData) => {
        setFees((prev: FeeData[]) => {
            const index = prev?.findIndex(f => f.feeType === payload.feeType);
            const next = [...prev];

            if (index !== -1) next[index] = payload;

            return next;
        });
    }, [setFees]);

    const updateFees = useCallback(async (feeType: number, feePayload: FeeData) => {
        try {
            await planMutation({
                service: 'customInvestmentService',
                API: 'updateFees',
                payload: { investmentApplicationId: investmentAppId, payload: feePayload },
            });
            updateFeesLocally(feePayload);
            notification.open({ content: t('shadowAccount.depot.feeUpdatedSuccessfully'), type: 'success' });
        } catch {
            // error notification handled by useGenericAPIMutation onError
        }
    }, [investmentAppId, planMutation, updateFeesLocally, t]);

    const updateIssueCommission = useCallback(async (issueCommission: string) => {
        try {
            await planMutation({
                service: 'planService',
                API: 'updateIssueCommission',
                payload: { portfolioId, issueCommission },
            });
            refetchPlanDetails();
            notification.open({ content: t('shadowAccount.depot.issueCommissionChanged'), type: 'success' });
            setModalVersion('');
        } catch {
            // error notification handled by useGenericAPIMutation onError
        }
    }, [portfolioId, planMutation, refetchPlanDetails, t]);

    // --- Form Handling ---
    const schema: ObjectSchema<FormValues> = useMemo(() => object().shape({
        newlySelectedValue: string().max(9, t('shadowAccount.depot.pbNumberMaxLength')).required(t('validation.mandatoryField')),
        agentId: string().when([], {
            is: () => modalVersion === DEPOSIT_MODAL_TYPE.pbNumber,
            then: (s) => s.required(t('validation.mandatoryField')),
            otherwise: (s) => s.nullable(),
        }) as any,
    }), [t, modalVersion]);

    const resolver = useYupValidationResolver(schema);

    const {
        control, handleSubmit, formState: { errors }, setValue, watch,
    } = useForm<FormValues>({
        resolver,
        defaultValues: {
            newlySelectedValue: '',
            agentId: '',
        },
    });

    const { newlySelectedValue } = watch();

    const handleSaveData = handleSubmit(async (values) => {
        if (modalVersion === DEPOSIT_MODAL_TYPE.commission) {
            await updateIssueCommission(values.newlySelectedValue);
        } else if (modalVersion === DEPOSIT_MODAL_TYPE.pbNumber) {
            const hasError = validateInputAlphanumeric(vstNumber, true);

            if (hasError) {
                setVstNumberError(hasError);

                return;
            }
            setVstNumberError(null);
            await updatePbNumber(values);
        } else {
            await updateGeneralAgency(values.newlySelectedValue);
        }
    });

    const showFeesHistoryModal = useCallback((feeType: string) => {
        setSelectedFeeType(feeType);
        setChangeHistoryType(CHANGE_HISTORY_TYPE.commission);
    }, []);

    const generalAgenciesOptions = useMemo(() => [
        { label: t('position.pleaseSelect'), id: '' },
        ...(generalAgencies?.map(i => ({ label: i.name, id: String(i.id) })) || []),
    ], [generalAgencies, t]);

    // --- Render Helpers ---
    const pbNumberForm = useMemo(() => (
        <>
            <Controller
                name="newlySelectedValue"
                control={control}
                render={({ field }) => (
                    <PbNumber
                        label={t('shadowAccount.depot.changePbNumber')}
                        required
                        {...field}
                        error={errors?.newlySelectedValue?.message}
                        setAgentInfo={() => {}}
                        onSelect={(val: string) => setValue('newlySelectedValue', val, { shouldValidate: true })}
                        includeVstNumber
                        vstNumberValue={vstNumber}
                        onVstNumberChange={(val: string) => setVstNumber(val)}
                        vstNumberError={vstNumberError}
                    />
                )}
            />

            <Controller
                name="agentId"
                control={control}
                render={({ field }) => (
                    <Select
                        {...field}
                        className="agent-info"
                        options={agentsListOptions}
                        innerRef={field.ref}
                        label={t('shadowAccount.depot.changeAdvisor')}
                        required
                        error={errors?.agentId?.message}
                        hasSearch
                        asyncSearch={getAgentInformation}
                        searchInProgress={searchAgentsInProgress}
                    />
                )}
            />
        </>
    ), [t, control, errors, vstNumber, vstNumberError, agentsListOptions, getAgentInformation, searchAgentsInProgress, setValue]);

    const generalAgencyForm = useMemo(() => (
        <>
            <div className="row-data">
                <span>{t('shadowAccount.depot.oldGeneralAgency')}</span>
                <span>{`${planDetails?.generalAgency?.vstNumber} - ${planDetails?.generalAgency?.name}`}</span>
            </div>
            <Controller
                name="newlySelectedValue"
                control={control}
                render={({ field }) => (
                    <SelectElement
                        {...field}
                        onChange={(val: string) => setValue('newlySelectedValue', val, { shouldValidate: true })}
                        value={newlySelectedValue}
                        options={generalAgenciesOptions}
                        hasSearch
                        error={errors?.newlySelectedValue?.message}
                    />
                )}
            />
        </>
    ), [t, planDetails, control, newlySelectedValue, generalAgenciesOptions, errors, setValue]);

    const shouldShowModifiedIssueCommision = useMemo(() => isAzp || isAzpAlt || isZiv || isZifd, [isAzp, isAzpAlt, isZiv, isZifd]);

    const commissionOptions = useMemo(
        () => [
            { label: t('position.pleaseSelect'), id: '' },
            ...((shouldShowModifiedIssueCommision ? staticIssuecommissionOptions : issueCommissionOptions)
                ?.map((i: any) => ({ label: i.label, id: i.id })) || [])
                .sort((a: any, b: any) => parseFloat(b.id) - parseFloat(a.id))],
        [t, shouldShowModifiedIssueCommision, staticIssuecommissionOptions, issueCommissionOptions],
    );

    const issueCommissionForm = useMemo(() => (
        <>
            <div className="row-data">
                <span>{t('shadowAccount.depot.oldIssueCommission')}</span>
                <span>{`${planDetails?.issueCommission}%`}</span>
            </div>
            <Controller
                name="newlySelectedValue"
                control={control}
                render={({ field }) => (
                    <SelectElement
                        {...field}
                        onChange={(val: string) => setValue('newlySelectedValue', val, { shouldValidate: true })}
                        value={newlySelectedValue}
                        options={commissionOptions}
                        hasSearch
                        error={errors?.newlySelectedValue?.message}
                    />
                )}
            />
        </>
    ), [t, planDetails, control, newlySelectedValue, commissionOptions, errors, setValue]);

    const modalContentByType: Record<string, ReactNode> = {
        [DEPOSIT_MODAL_TYPE.pbNumber]: pbNumberForm,
        [DEPOSIT_MODAL_TYPE.generalAgency]: generalAgencyForm,
        [DEPOSIT_MODAL_TYPE.commission]: issueCommissionForm,
    };

    return (
        <Preloader isLoading={detailsLoading} error={detailsError || membersError}>
            <dl className="DataList DataList--inline depot-info">
                <div>
                    <dt>{t('shadowAccount.depot.refNumber')}</dt>
                    <dd>{planDetails?.lpzPlanId}</dd>
                </div>
                <div>
                    <dt>IBAN</dt>
                    <dd>{planDetails?.accountIban?.replace(/.{1,4}/g, '$& ')}</dd>
                </div>
                <div>
                    <dt>{t('shadowAccount.depot.openDate')}</dt>
                    <dd>{getFormattedDate(planDetails?.openingDate)}</dd>
                </div>
                <div>
                    <dt>{t('shadowAccount.depot.closeDate')}</dt>
                    <dd>{getFormattedDate(planDetails?.closingDate) || '-'}</dd>
                </div>
                {
                    custodyAmFee.map(item => {
                        const feeValue = fees?.find((fee: FeeData) => fee.feeType === item.type)?.fee;

                        return (
                            <div key={item.type}>
                                <dt>{getFeeLabel(item.type, t)}</dt>
                                <dd>{feeValue ? `${feeValue}%` : '-'}</dd>
                                {!isZic && (
                                    <dt>
                                        {(item.isEditable && hasAccessPolicy(FEES_ACCESS_POLICIES[item.type])) && isPortfolioOnReadOnly && (
                                            <>
                                                <Button type="link" onClick={() => setFeeModal(item.type)}>
                                                    <Icon type="edit" />
                                                    {t('interaction.edit')}
                                                </Button>
                                                <span className="splitter">|</span>
                                            </>
                                        )}
                                        <Icon type="history" onClick={() => showFeesHistoryModal(item.type)} />
                                    </dt>
                                )}
                            </div>
                        );
                    })
                }
                {!isPk && !isZic && (
                    <div>
                        <dt>{t('shadowAccount.depot.commission')}</dt>
                        <dd>{`${planDetails?.issueCommission?.toFixed(1)}%`}</dd>
                        <dt>
                            {hasAccessPolicy('ChangePBNumber') && isPortfolioOnReadOnly && (
                                <>
                                    <Button type="link" onClick={() => setModalVersion(DEPOSIT_MODAL_TYPE.commission)}>
                                        <Icon type="edit" />
                                        {t('interaction.edit')}
                                    </Button>
                                    <span className="splitter">|</span>
                                </>
                            )}
                            <Icon type="history" onClick={() => showFeesHistoryModal(FEE_TYPES.issueCommission)} />
                        </dt>
                    </div>
                )}
                <div>
                    <dt>{t('shadowAccount.depot.pbNumber')}</dt>
                    <dd>{`${planDetails?.pbNumber} - ${getAgentInfo(t, planDetails?.memberFullName, planDetails?.provisionReceiver)}`}</dd>
                    <dt>
                        {hasAccessPolicy('ChangePBNumber') && isPortfolioOnReadOnly && (
                            <>
                                <Button type="link" onClick={() => setModalVersion(DEPOSIT_MODAL_TYPE.pbNumber)}>
                                    <Icon type="edit" />
                                    {t('interaction.edit')}
                                </Button>
                                <span className="splitter">|</span>
                            </>
                        )}
                        <Icon type="history" onClick={() => setChangeHistoryType(CHANGE_HISTORY_TYPE.pbNumber)} />
                    </dt>
                </div>
                <div>
                    <dt>{t('portfolio.createNewPortfolio.agentInformation.generalAgency')}</dt>
                    <dd>{joinWithSeparator(' - ', planDetails?.generalAgency?.vstNumber, planDetails?.generalAgency?.name)}</dd>
                    <dt>
                        {hasAccessPolicy('ChangePBNumber') && isPortfolioOnReadOnly && (
                            <>
                                <Button type="link" onClick={() => setModalVersion(DEPOSIT_MODAL_TYPE.generalAgency)}>
                                    <Icon type="edit" />
                                    {t('interaction.edit')}
                                </Button>
                                <span className="splitter">|</span>
                            </>
                        )}
                        <Icon type="history" onClick={() => setChangeHistoryType(CHANGE_HISTORY_TYPE.generalAgency)} />
                    </dt>
                </div>
            </dl>

            {!!modalVersion && (
                <Modal
                    visible
                    title={t(`shadowAccount.depot.change${modalVersion}Title`)}
                    onCancel={() => setModalVersion('')}
                    onOk={handleSaveData}
                    width={550}
                    okText={saveInProgress ? <Loader /> : t(`shadowAccount.depot.save${modalVersion}Button`)}
                    className="change-pb-number"
                    okButtonProps={{ disabled: saveInProgress }}
                    keyboard={false}
                    maskClosable={false}
                    closable
                    closeIcon={<Icon type={ICON_TYPE_CLOSE_SLIM} size={20} />}
                >
                    {modalContentByType[modalVersion]}
                </Modal>
            )}

            {changeHistoryType && (
                <ChangeHistoryModal
                    type={changeHistoryType}
                    onClose={() => setChangeHistoryType(null)}
                    portfolioId={portfolioId}
                    selectedFeeType={selectedFeeType}
                    investmentApplicationId={investmentAppId}
                />
            )}

            <CustodyFeeModal
                showModal={feeModal === FEE_TYPES.custody}
                showDeleteButton
                handleClose={() => setFeeModal(null)}
                onSave={updateFees}
                onDeleteFee={updateFeesLocally}
                feeData={fees?.find((f: FeeData) => f.feeType === feeModal)}
                defaultCustodyFee={modelPortfolioSettings?.custodyFee}
            />

            <AssetFeeModal
                showModal={feeModal === FEE_TYPES.asset}
                showDeleteButton
                handleClose={() => setFeeModal(null)}
                onSave={updateFees}
                onDeleteFee={updateFeesLocally}
                feeData={fees?.find((f: FeeData) => f.feeType === feeModal)}
                isZivAzp={isZivAzp}
                defaultAssetFee={defaultAssetFee as number}
                previousAssetManagementFee={previousAssetManagementFee}
            />

            <AdministrationFeeModal
                showModal={feeModal === FEE_TYPES.administration}
                handleClose={() => setFeeModal(null)}
                onSave={updateFees}
                feeData={findSpecificFeeData(FEE_TYPES.administration, allFees)}
                defaultAdministrationFee={modelPortfolioSettings.AdministrationFee}
            />
        </Preloader>
    );
};

export default FzDepotInformationKpi;
